import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { SerializedStyles, css } from '@emotion/core'
import { useWindowSize } from 'react-use'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoadingFull } from '@/components/molecules/loading-full'
import { sectionHeaderStyle } from '@/components/organisms/dashboard/style'
import { wholeContainerStyle, mainColumnStyle, listHeaderGrayStyle, listContentsWrapperStyle } from '@/constants/layout'
import { SideMenu } from '@/components/organisms/side-menu'
import { Header } from '@/components/organisms/header'
import { AccountContext } from '@/contexts/account'
import * as api from '@/apis/aipass'
import { FileMessageType, CsvUploadHistoryResponseType } from '@/models/csv-upload-history/csv-upload-history'
import { isStaffGuardCancelledError, isStaffGuardSessionReauthAbortError } from '@/libs/errors'

export const UploadHistory: React.FC = () => {
  const { t } = useTranslation()
  const windows = useWindowSize()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [uploadCsvHistory, setUploadCsvHistory] = useState<CsvUploadHistoryResponseType[]>([])
  const { account } = useContext<any>(AccountContext)
  const history = useHistory()
  const { errorHandler } = useErrorHandler()

  // Get CSV Upload list
  const fetchUploadCsvHistory = async (shouldRetryAfterReauth = true): Promise<void> => {
    try {
      setIsLoading(true)
      const res = await api.fetchUploadCsvHistory()
      setUploadCsvHistory(res || [])
    } catch (error) {
      if (isStaffGuardSessionReauthAbortError(error) && shouldRetryAfterReauth) {
        await fetchUploadCsvHistory(false)
        return
      }
      if (isStaffGuardCancelledError(error)) return
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const computedUploadStatusMessageType = (id: string, uploadStatus: boolean, errorMessage: string): string => {
    if (errorMessage) {
      const errors = JSON.parse(errorMessage).map(error => error?.line)
      const errorStrings = errors[0] !== 0 ? errors.map(e => e + t('Error line')).join(', ') : t('Failed to upload csv')
      return `${t('Upload file type error')} (${errorStrings})`
    }
    if (!uploadStatus) {
      return t('Upload file type execution')
    }
    return t('Upload file type complete')
  }

  const computedUploadStatusStyle = (uploadStatus: boolean, errorMessage: string): SerializedStyles => {
    if (errorMessage) {
      return dataStatusErrorTypeStyle
    }
    return uploadStatus ? dataStatusCompleteTypeStyle : dataStatusTypeStyle
  }

  useEffect(() => {
    if (account && account?.hotel) {
      fetchUploadCsvHistory()
    }
  }, [account])

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchUploadCsvHistory()
    }, 30000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div css={mainColumnStyle}>
        <Header title={t('Accommodation management')} isBack goBack={history.goBack} />
        <div
          css={settingMainContainerStyle}
          style={{
            minHeight: windows.height - 180,
          }}
        >
          <div
            css={mainContainerStyle}
            style={{
              minHeight: windows.height - 105,
            }}
          >
            <div css={headerContainerStyle}>
              <div css={headerStyle}>{t('Import history')}</div>
            </div>
            <ul css={listHeaderStyle}>
              <li css={dataCreateDateStyle}>{t('Creation date')}</li>
              <li css={dataFileTypeStyle}>{t('Upload file type')}</li>
              <li css={dataFileNameStyle}>{t('Upload file name')}</li>
              <li css={dataStatusTypeStyle}>{t('Upload status')}</li>
            </ul>
            <div
              css={listContentsWrapperStyle}
              style={{
                maxHeight: windows.height - 180,
              }}
            >
              <div css={customListContentsStyle}>
                {uploadCsvHistory.map(v => {
                  return (
                    <div key={v.id} css={customListItemUnreadStyle}>
                      <div css={dataCreateDateStyle}>{dayjs(v.createdAt).format('MM月DD日 HH:mm')}</div>
                      <div css={dataFileTypeStyle}>{t(FileMessageType[v.fileType])}</div>
                      <div css={dataFileNameStyle}>{v.fileName}</div>
                      <div css={computedUploadStatusStyle(v.uploadStatus, v.errorMessage)}>
                        {computedUploadStatusMessageType(v.id, v.uploadStatus, v.errorMessage)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const settingMainContainerStyle = css({
  padding: 24,
})

const mainContainerStyle = css({
  width: '100%',
  borderRadius: 5,
  boxShadow: '0px 0px 6px #0000001a',
  background: '#FFF',
  overflow: 'hidden',
})

const headerStyle = css(sectionHeaderStyle, {
  height: 50,
  marginLeft: 28,
})

const headerContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: '#FFF',
})

const listHeaderStyle = css(listHeaderGrayStyle, {
  padding: '0 32px',
})

const headTitleStyle = css({
  display: 'flex',
  alignItems: 'center',
})

const dataCreateDateStyle = css(headTitleStyle, {
  width: '12%',
})

const dataFileTypeStyle = css(headTitleStyle, {
  width: '8%',
})

const dataFileNameStyle = css(headTitleStyle, {
  marginRight: 32,
  width: '20%',
})

const dataStatusTypeStyle = css(headTitleStyle, {
  fontWeight: 'bold',
  width: '60%',
})

const dataStatusCompleteTypeStyle = css(headTitleStyle, {
  fontWeight: 'bold',
  width: '60%',
  color: '#F2A40B',
})

const dataStatusErrorTypeStyle = css(headTitleStyle, {
  fontWeight: 'bold',
  width: '60%',
  color: '#F44F42',
})

const customListContentsStyle = css({
  fontSize: 14,
})

const customListItemUnreadStyle = css({
  padding: '16px 32px',
  display: 'flex',
  borderBottom: '1px solid #f2f2f2',
  width: '100%',
  alignItems: 'center',
})
